// src/features/marketplace/pages/ListingDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db, auth } from '../../../firebase';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { 
  ArrowLeft, MapPin, Calendar, Gauge, Fuel, MessageCircle, 
  CheckCircle, Share2, ChevronLeft, ChevronRight, Phone, Eye, Car
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { formatPrice, formatMileage } from '../../../utils/helpers';
import { ListingCard } from '../components/ListingCard';

export const ListingDetail = ({ showToast, setZoomImg }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerPrice, setOfferPrice] = useState('');
  const [showPhone, setShowPhone] = useState(false);
  const [showVin, setShowVin] = useState(false);
  const [relatedListings, setRelatedListings] = useState([]);

  useEffect(() => {
    const loadListing = async () => {
      try {
        const docSnap = await getDoc(doc(db, "marketplace_listings", id));
        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() };
          setListing(data);
          
          const q = query(
            collection(db, "marketplace_listings"),
            where("status", "==", "active"),
            orderBy("createdAt", "desc"),
            limit(20)
          );
          
          onSnapshot(q, (snap) => {
            const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            const related = all.filter(item => 
              item.id !== id && (
                item.brand === data.brand || 
                item.fuel === data.fuel
              )
            ).slice(0, 4);
            setRelatedListings(related);
          });
        } else {
          navigate('/market');
        }
      } catch (error) {
        console.error("Błąd ładowania:", error);
        navigate('/market');
      } finally {
        setLoading(false);
      }
    };
    loadListing();
  }, [id, navigate]);

  const handleContactSeller = async () => {
    if (!auth.currentUser) return showToast("Zaloguj się aby kontaktować", "error");
    if (auth.currentUser.uid === listing.userId) return showToast("To Twoje ogłoszenie", "error");

    const conversationId = `${auth.currentUser.uid}_${listing.userId}_${listing.id}`;
    try {
      await setDoc(doc(db, "marketplace_conversations", conversationId), {
        participants: [auth.currentUser.uid, listing.userId],
        listingId: listing.id,
        listingTitle: listing.title,
        listingImage: listing.images?.[0] || null,
        lastMessage: "",
        lastMessageAt: serverTimestamp(),
        unreadCount: { [auth.currentUser.uid]: 0, [listing.userId]: 0 },
        createdAt: serverTimestamp()
      });
      navigate(`/market/messages/${conversationId}`);
    } catch (error) {
      console.error("Błąd tworzenia konwersacji:", error);
      showToast("Błąd otwierania czatu", "error");
    }
  };

  const handleMakeOffer = async () => {
    if (!auth.currentUser) return showToast("Zaloguj się aby składać oferty", "error");
    if (!offerPrice || parseInt(offerPrice) <= 0) return showToast("Podaj poprawną cenę", "error");

    const conversationId = `${auth.currentUser.uid}_${listing.userId}_${listing.id}`;
    
    console.log('=== MAKE OFFER DEBUG ===');
    console.log('conversationId:', conversationId);
    console.log('fromUser:', auth.currentUser.uid);
    console.log('toUser:', listing.userId);
    console.log('listingId:', listing.id);
    console.log('offerPrice:', offerPrice);
    
    try {
      // 1. Stwórz conversation
      console.log('Creating conversation...');
      await setDoc(doc(db, "marketplace_conversations", conversationId), {
        participants: [auth.currentUser.uid, listing.userId],
        listingId: listing.id,
        listingTitle: listing.title,
        listingImage: listing.images?.[0] || null,
        lastMessage: `Oferuję ${formatPrice(parseInt(offerPrice))}`,
        lastMessageAt: serverTimestamp(),
        unreadCount: { [auth.currentUser.uid]: 0, [listing.userId]: 1 },
        createdAt: serverTimestamp()
      });
      console.log('✅ Conversation created');

      // 2. Stwórz message
      const messageId = `msg_${Date.now()}`;
      console.log('Creating message:', messageId);
      await setDoc(doc(db, "marketplace_messages", messageId), {
        conversationId,
        fromUser: auth.currentUser.uid,
        toUser: listing.userId,
        listingId: listing.id,
        content: `Oferuję ${formatPrice(parseInt(offerPrice))}`,
        isOffer: true,
        offerPrice: parseInt(offerPrice),
        createdAt: serverTimestamp(),
        read: false
      });
      console.log('✅ Message created');

      showToast("Oferta wysłana!");
      setShowOfferModal(false);
      
      // 3. Navigate po chwili (żeby Firestore zdążył)
      setTimeout(() => {
        navigate(`/market/messages/${conversationId}`);
      }, 500);
      
    } catch (error) {
      console.error("❌ BŁĄD OFERTY:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      showToast(`Błąd: ${error.message}`, "error");
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    showToast("Link skopiowany!");
  };

  const nextImage = () => {
    if (listing.images?.length > 1)
      setCurrentImageIndex((prev) => (prev + 1) % listing.images.length);
  };

  const prevImage = () => {
    if (listing.images?.length > 1)
      setCurrentImageIndex((prev) => (prev - 1 + listing.images.length) % listing.images.length);
  };

  if (loading) return <div className="p-12 text-center text-gray-500">Ładowanie...</div>;
  if (!listing) return null;

  // Sprawdź czy mamy współrzędne do mapy
  const hasLocation = listing.location?.lat && listing.location?.lng;
  const mapLat = listing.location?.lat;
  const mapLng = listing.location?.lng;
  const locationName = listing.location?.name || 'Polska';

  // SEO meta description
  const metaDesc = `${listing.make} ${listing.model} ${listing.year}r., ${formatMileage(listing.mileage)}km, ${listing.fuel}, ${formatPrice(listing.price)}. ${listing.description?.substring(0, 100) || ''}...`;

  return (
    <>
      <Helmet>
        <title>{listing.title} - {formatPrice(listing.price)} | Motoradar24 Giełda</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={`${listing.make} ${listing.model} sprzedaż, ${listing.make} na sprzedaż, ${listing.model} ${listing.year}, auto premium, ${locationName}`} />
        
        <meta property="og:title" content={`${listing.title} - ${formatPrice(listing.price)}`} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://motoradar24.pl/market/car/${listing.id}`} />
        {listing.images?.[0] && <meta property="og:image" content={listing.images[0]} />}
        <meta property="product:price:amount" content={listing.price} />
        <meta property="product:price:currency" content="PLN" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pb-32">
      {/* Offer Modal - ✅ MOBILE FIX */}
      {showOfferModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[900] flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-3xl p-6 animate-slideUp">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 md:hidden"></div> {/* Handle dla mobile */}
            <h3 className="text-xl font-bold mb-4">Zaproponuj cenę</h3>
            <p className="text-sm text-gray-600 mb-4">
              Cena wywoławcza: <span className="font-bold text-emerald-600">{formatPrice(listing.price)}</span>
            </p>
            <input
              type="number"
              placeholder="Twoja oferta..."
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-lg font-bold outline-none focus:ring-2 focus:ring-emerald-500 mb-4"
              value={offerPrice}
              onChange={(e) => setOfferPrice(e.target.value)}
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowOfferModal(false)}
                className="flex-1 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-semibold transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={handleMakeOffer}
                className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-teal-700 transition-all"
              >
                Wyślij ofertę
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/market')} className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
            <ArrowLeft size={20}/>
            <span className="font-semibold">Wróć</span>
          </button>
          <button onClick={handleShare} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <Share2 size={20} className="text-gray-600"/>
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 overflow-hidden">
        {/* Image Gallery */}
        <div className="bg-white rounded-2xl overflow-hidden mb-6 border border-gray-200 max-w-full">
          <div className="relative aspect-video bg-gray-100">
            {listing.images && listing.images.length > 0 ? (
              <>
                <img
                  src={listing.images[currentImageIndex]}
                  alt={`${listing.make} ${listing.model} ${listing.year} - zdjęcie ${currentImageIndex + 1} z ${listing.images.length} - ${formatPrice(listing.price)} - Motoradar24`}
                  className="w-full h-full object-cover cursor-zoom-in"
                  onClick={() => setZoomImg(listing.images[currentImageIndex])}
                  loading="lazy"
                />
                {listing.images.length > 1 && (
                  <>
                    <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full transition-all">
                      <ChevronLeft size={24}/>
                    </button>
                    <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full transition-all">
                      <ChevronRight size={24}/>
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {listing.images.length}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">Brak zdjęcia</div>
            )}
          </div>

          {listing.images && listing.images.length > 1 && (
            <div className="flex gap-2 p-4 overflow-x-auto">
              {listing.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === currentImageIndex ? 'border-emerald-600' : 'border-gray-200'
                  }`}
                >
                  <img src={img} alt={`${listing.make} ${listing.model} miniaturka ${idx + 1}`} className="w-full h-full object-cover" loading="lazy"/>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 max-w-full overflow-hidden">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6 max-w-full overflow-hidden">
            {/* Title & Price */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 max-w-full overflow-hidden">
              <h1 className="text-3xl font-black text-gray-900 mb-4 break-words">{listing.title}</h1>
              <div className="flex items-center gap-4 mb-6">
                <div className="text-4xl font-black text-emerald-600">
                  {formatPrice(listing.price)}
                </div>
                {listing.status === 'active' && (
                  <div className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-semibold flex items-center gap-1">
                    <CheckCircle size={16}/>
                    Dostępne
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Calendar size={16}/>
                    <span className="text-xs font-semibold">Rok</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{listing.year}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Gauge size={16}/>
                    <span className="text-xs font-semibold">Przebieg</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{formatMileage(listing.mileage)}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Fuel size={16}/>
                    <span className="text-xs font-semibold">Paliwo</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900 capitalize">{listing.fuel}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <MapPin size={16}/>
                    <span className="text-xs font-semibold">Lokalizacja</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{locationName}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            {listing.description && (
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Opis</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words max-w-full overflow-hidden">{listing.description}</p>
              </div>
            )}

            {/* MAPA LOKALIZACJI */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="text-emerald-600" size={24}/>
                Lokalizacja
              </h2>

              {/* Nazwa miejscowości zawsze widoczna */}
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span className="font-semibold text-gray-900 text-lg">{locationName}</span>
              </div>

              {/* Mapa jeśli mamy współrzędne */}
              {hasLocation ? (
                <div className="h-64 rounded-xl overflow-hidden border border-gray-200 shadow-inner">
                  <MapContainer
                    center={[mapLat, mapLng]}
                    zoom={13}
                    className="h-full w-full"
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    <Marker position={[mapLat, mapLng]}>
                      <Popup>
                        <div className="text-center">
                          <p className="font-bold text-emerald-600">{listing.title}</p>
                          <p className="text-sm text-gray-600">{locationName}</p>
                          <p className="font-black text-emerald-700">{formatPrice(listing.price)}</p>
                        </div>
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              ) : (
                // Brak dokładnych współrzędnych - pokazuj tylko nazwę
                <div className="h-32 rounded-xl bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <MapPin size={32} className="mx-auto mb-2 text-gray-300"/>
                    <p className="text-sm font-semibold">Lokalizacja przybliżona</p>
                    <p className="text-xs">{locationName}</p>
                  </div>
                </div>
              )}
            </div>

            {/* VIN - Hidden by default */}
            {listing.vin && (
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4">VIN</h2>
                {showVin ? (
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <p className="font-mono text-lg font-bold text-gray-900">{listing.vin}</p>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowVin(true)}
                    className="w-full py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold text-gray-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Eye size={20}/>
                    Pokaż VIN
                  </button>
                )}
              </div>
            )}

            {/* RELATED LISTINGS */}
            {relatedListings.length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Car size={24} className="text-emerald-600"/>
                    Podobne ogłoszenia
                  </h2>
                  <button
                    onClick={() => navigate('/market/search')}
                    className="text-sm font-semibold text-emerald-600 hover:text-emerald-700"
                  >
                    Zobacz więcej →
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {relatedListings.map(item => (
                    <div
                      key={item.id}
                      onClick={() => {
                        window.scrollTo(0, 0);
                        navigate(`/market/car/${item.id}`);
                      }}
                      className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:border-emerald-500 hover:shadow-lg transition-all cursor-pointer group"
                    >
                      <div className="aspect-video overflow-hidden bg-gray-100">
                        <img
                          src={item.images?.[0] || '/placeholder-car.jpg'}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-sm text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors line-clamp-1">
                          {item.title}
                        </h3>
                        <p className="text-lg font-black text-emerald-600 mb-2">
                          {formatPrice(item.price)}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{item.year}</span>
                          <span>•</span>
                          <span>{formatMileage(item.mileage)}</span>
                          {item.location?.name && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <MapPin size={10}/>
                                {item.location.name}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Contact */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 sticky top-20 space-y-4">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Kontakt</h3>
              
              {/* ✅ FIX: Ukryj przyciski dla własnych ogłoszeń */}
              {listing.userId === auth.currentUser?.uid ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                  <p className="text-sm font-semibold text-blue-900 mb-1">To Twoje ogłoszenie</p>
                  <p className="text-xs text-blue-700">Zarządzaj nim w "Moje Ogłoszenia"</p>
                  <button
                    onClick={() => navigate('/market/my-listings')}
                    className="mt-3 w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Przejdź do zarządzania
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleContactSeller}
                    className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-teal-700 transition-all flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={20}/>
                    Napisz do sprzedawcy
                  </button>

                  <button
                    onClick={() => setShowOfferModal(true)}
                    className="w-full py-4 bg-white border-2 border-emerald-600 text-emerald-600 rounded-xl font-bold hover:bg-emerald-50 transition-all flex items-center justify-center gap-2"
                  >
                    💰 Zaproponuj cenę
                  </button>
                </>
              )}

              {/* Phone - dostępny dla wszystkich */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Telefon</p>
                {showPhone ? (
                  <a 
                    href={`tel:${listing.phone}`}
                    className="block text-lg font-bold text-emerald-600 hover:text-emerald-700"
                  >
                    {listing.phone}
                  </a>
                ) : (
                  <button
                    onClick={() => setShowPhone(true)}
                    className="w-full py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold text-gray-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Phone size={18}/>
                    POKAŻ NUMER
                  </button>
                )}
              </div>

              {/* Lokalizacja w sidebarze */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Lokalizacja</p>
                <p className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                  <MapPin size={14} className="text-emerald-600"/>
                  {locationName}
                </p>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Dodano</p>
                <p className="text-sm font-semibold text-gray-900">
                  {listing.createdAt && new Date(listing.createdAt.seconds * 1000).toLocaleDateString('pl-PL', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>

              {listing.duration && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Aktywne przez</p>
                  <p className="text-sm font-semibold text-gray-900">{listing.duration} dni</p>
                </div>
              )}

              <div>
                <p className="text-xs text-gray-500 mb-2">Ogłoszenie ID</p>
                <p className="text-sm font-mono text-gray-600">#{listing.id.substring(0, 8)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
  );
};